/**
 * Dashboard Plugin System - Tab Resolver
 * 
 * Intelligently resolves tabs by:
 * 1. Collecting default tabs from component
 * 2. Applying plugin tabs (new, override, extend)
 * 3. Applying visibility rules
 * 4. Applying content overrides
 * 5. Sorting by order property
 * 6. Filtering hidden tabs
 */

import type { TabConfig, TabVisibilityRule, PluginRegistry, ContentOverrideConfig } from './dashboard-types';
import { pluginRegistry } from './dashboard-registry';

export class TabResolver {
  private registry: PluginRegistry;

  constructor(registry?: PluginRegistry) {
    this.registry = registry || pluginRegistry;
  }

  private getChannelType(context: any): string | undefined {
    return context?.inbox?.channel_type || context?.inbox?.channelType;
  }

  private getProvider(context: any): string | undefined {
    return (
      context?.inbox?.provider ||
      context?.inbox?.additional_attributes?.provider ||
      context?.inbox?.additionalAttributes?.provider ||
      context?.inbox?.channel?.provider ||
      context?.inbox?.channel?.additional_attributes?.provider ||
      context?.inbox?.channel?.additionalAttributes?.provider ||
      // medium field is a direct DB column (always present), used by QuePasa and others
      // to identify sub-type within a channel (e.g. medium='quepasa' for Channel::Api)
      // This matches the logic in inboxHelpers.getInboxProvider
      context?.inbox?.medium
    );
  }

  private getMedium(context: any): string | undefined {
    return context?.inbox?.medium;
  }

  /**
   * Main resolution method
   * Resolves tabs for a component with context
   */
  resolve(componentName: string, defaultTabs: TabConfig[], context: any): TabConfig[] {
    let tabs = [...defaultTabs];

    // 1. Apply plugin tabs (new, override, extend)
    tabs = this.applyPluginTabs(componentName, tabs, context);

    // 2. Apply visibility rules
    tabs = this.applyVisibilityRules(componentName, tabs, context);

    // 3. Apply ordering
    tabs = this.applyOrdering(tabs);

    // 4. Filter hidden tabs
    tabs = tabs.filter(t => !t.hidden);

    return tabs;
  }

  /**
   * Apply plugin tabs (new, override, extend modes)
   */
  private applyPluginTabs(componentName: string, tabs: TabConfig[], context: any): TabConfig[] {
    const pluginTabs = this.registry.tabs[componentName] || [];

    for (const pluginTab of pluginTabs) {
      // Check condition
      if (pluginTab.condition && !pluginTab.condition(context)) {
        continue;
      }

      // Apply legacy filters (channelType, provider, medium)
      if (!this.matchesFilters(pluginTab, context)) {
        continue;
      }

      const mode = pluginTab.mode || 'new';

      switch (mode) {
        case 'override':
          // Replace existing tab
          const overrideIndex = tabs.findIndex(t => t.key === (pluginTab.overrideKey || pluginTab.key));
          if (overrideIndex >= 0) {
            tabs[overrideIndex] = { ...pluginTab };
          } else {
            console.warn(
              `[TabResolver] Override target not found: ${pluginTab.overrideKey || pluginTab.key} in ${componentName}`
            );
          }
          break;

        case 'extend':
          // Extend existing tab (future feature)
          this.extendTab(tabs, pluginTab);
          break;

        case 'new':
        default:
          // Add new tab
          tabs.push({ ...pluginTab });
          break;
      }
    }

    return tabs;
  }

  /**
   * Apply visibility rules (hide, show, show-only)
   */
  private applyVisibilityRules(componentName: string, tabs: TabConfig[], context: any): TabConfig[] {
    const rules = this.registry.tabVisibility.filter(r => r.componentName === componentName);

    for (const rule of rules) {
      // Check condition
      if (rule.condition && !rule.condition(context)) {
        continue;
      }

      switch (rule.mode) {
        case 'hide':
          // Hide specific tabs
          rule.tabKeys.forEach(key => {
            const tab = tabs.find(t => t.key === key);
            if (tab) {
              tab.hidden = true;
            }
          });
          break;

        case 'show':
          // Show specific tabs (unhide)
          rule.tabKeys.forEach(key => {
            const tab = tabs.find(t => t.key === key);
            if (tab) {
              tab.hidden = false;
            }
          });
          break;

        case 'show-only':
          // Hide all tabs except specified ones
          tabs.forEach(tab => {
            tab.hidden = !rule.tabKeys.includes(tab.key);
          });
          break;
      }
    }

    return tabs;
  }

  /**
   * Apply ordering (sort by order property)
   */
  private applyOrdering(tabs: TabConfig[]): TabConfig[] {
    return tabs.sort((a, b) => {
      const orderA = a.order ?? 999;
      const orderB = b.order ?? 999;
      return orderA - orderB;
    });
  }

  /**
   * Get content for a specific tab (with override support)
   */
  getContentForTab(componentName: string, tabKey: string, context: any): any {
    // Check content overrides first
    const overrides = this.registry.contentOverrides[componentName] || [];

    for (const override of overrides) {
      if (override.tabKey !== tabKey) {
        continue;
      }

      // Check condition
      if (override.condition && !override.condition(context)) {
        continue;
      }

      // Check legacy filters
      if (!this.matchesContentOverrideFilters(override, context)) {
        continue;
      }

      return override.component;
    }

    // Return null if no override found (component will use default)
    return null;
  }

  /**
   * Check if tab matches legacy filters (channelType, provider, medium)
   */
  private matchesFilters(tab: TabConfig, context: any): boolean {
    // Check channelType filter
    if (tab.channelType) {
      if (!context.inbox || this.getChannelType(context) !== tab.channelType) {
        return false;
      }
    }

    // Check provider filter
    if (tab.provider) {
      const provider = this.getProvider(context);

      if (!context.inbox || provider !== tab.provider) {
        return false;
      }
    }

    // Check medium filter
    if (tab.medium) {
      if (!context.inbox || this.getMedium(context) !== tab.medium) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check if content override matches filters
   */
  private matchesContentOverrideFilters(override: ContentOverrideConfig, context: any): boolean {
    // Check channelType filter
    if (override.channelType) {
      if (!context.inbox || this.getChannelType(context) !== override.channelType) {
        return false;
      }
    }

    // Check provider filter
    if (override.provider) {
      const provider = this.getProvider(context);

      if (!context.inbox || provider !== override.provider) {
        return false;
      }
    }

    return true;
  }

  /**
   * Extend existing tab (future feature - placeholder)
   */
  private extendTab(tabs: TabConfig[], pluginTab: TabConfig): void {
    const targetIndex = tabs.findIndex(t => t.key === (pluginTab.overrideKey || pluginTab.key));
    
    if (targetIndex < 0) {
      console.warn(
        `[TabResolver] Extend target not found: ${pluginTab.overrideKey || pluginTab.key}`
      );
      return;
    }

    // Future: Add logic to extend tab content based on position and targetSection
    // For now, just log the intent
    console.log(
      `[TabResolver] Extend mode not fully implemented yet for tab: ${pluginTab.key}`
    );
  }
}

/**
 * Create a singleton resolver instance
 */
let defaultResolver: TabResolver | null = null;

export function getTabResolver(): TabResolver {
  if (!defaultResolver) {
    defaultResolver = new TabResolver();
  }
  return defaultResolver;
}

/**
 * Reset the default resolver (useful for tests)
 */
export function resetTabResolver(): void {
  defaultResolver = null;
}
